
import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const missionRef = useRef<HTMLDivElement>(null);
  const visionRef = useRef<HTMLDivElement>(null);
  const valuesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = '삼원테크 | 회사소개 - 30년 전통 스테인리스 물탱크 전문 제조업체';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 회사소개. 1996년 창업 이래 30년간 스테인리스 물탱크 전문 제조. ISO9001·ISO14001·KS 인증 보유. 전남 화순 소재 물탱크 직접 생산·시공 전문기업.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
      "name": "삼원테크 회사소개",
      "description": "1996년 창업 이래 30년간 스테인리스 물탱크 전문 제조. ISO9001·ISO14001·KS 인증 보유.",
      "inLanguage": "ko-KR",
      "isPartOf": {
        "@type": "WebSite",
        "url": import.meta.env.VITE_SITE_URL || 'https://example.com'
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-about')?.remove(); };
  }, []);

  const stats = [
    { number: '30+', label: '년 업력', icon: 'ri-time-line' },
    { number: '5,000+', label: '시공 실적', icon: 'ri-building-line' },
    { number: '100%', label: '고객 만족도', icon: 'ri-star-line' },
    { number: '24/7', label: 'A/S 지원', icon: 'ri-customer-service-2-line' },
  ];

  const values = [
    { icon: 'ri-shield-check-line', title: '품질 최우선', description: '최고급 스테인리스 스틸과 엄격한 품질관리로 안전하고 위생적인 물탱크를 제공합니다', color: 'bg-teal-600' },
    { icon: 'ri-customer-service-line', title: '고객 중심', description: '고객의 요구사항을 정확히 파악하고 맞춤형 솔루션을 제공하여 최상의 만족을 드립니다', color: 'bg-blue-600' },
    { icon: 'ri-lightbulb-line', title: '기술 혁신', description: '지속적인 연구개발과 기술 혁신으로 업계 최고 수준의 제품을 생산합니다', color: 'bg-amber-600' },
    { icon: 'ri-leaf-line', title: '환경 보호', description: '친환경 소재와 공법을 사용하여 지속 가능한 미래를 만들어갑니다', color: 'bg-green-600' },
  ];

  const certifications = [
    { title: 'ISO 9001', description: '품질경영시스템 인증', icon: 'ri-award-line' },
    { title: 'ISO 14001', description: '환경경영시스템 인증', icon: 'ri-plant-line' },
    { title: 'KC 인증', description: '국가통합인증마크', icon: 'ri-checkbox-circle-line' },
    { title: '위생안전인증', description: '먹는물 수질기준 적합', icon: 'ri-water-flash-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[70vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/28ee486c9e1b112ec56131187fbd7560.jpg"
            alt="회사소개"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        </div>
        <div className="relative h-full flex flex-col items-center justify-center text-white px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl"
          >
            <span className="text-sm uppercase tracking-[3px] text-teal-400 font-semibold">About Us</span>
            <h1 className="text-6xl md:text-7xl font-bold mt-4 mb-6">깨끗한 물,<br />안전한 미래</h1>
            <p className="text-xl md:text-2xl leading-relaxed">
              30년 이상의 경험과 기술력으로 최고 품질의 물탱크를 제조하는<br />
              대한민국 대표 물탱크 전문 기업입니다
            </p>
          </motion.div>
        </div>
      </div>

      {/* CEO Message Section */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="px-4 py-24"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20korean%20business%20executive%20in%20modern%20office%20standing%20confidently%20wearing%20dark%20suit%20warm%20smile%20corporate%20portrait%20photography%20natural%20lighting&width=900&height=1200&seq=ceo-message-samwon&orientation=portrait"
                alt="대표이사"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
            <div>
              <span className="text-sm uppercase tracking-[2px] text-teal-600 font-semibold">CEO Message</span>
              <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-6">대표이사 인사말</h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>안녕하십니까. 삼원테크 대표이사 김철수입니다.</p>
                <p>저희 삼원테크는 1994년 설립 이래 30년 동안 오직 물탱크 제조 한 길만을 걸어왔습니다. 깨끗하고 안전한 물을 공급하는 것이 우리의 사명이라는 신념으로, 최고 품질의 스테인리스 스틸 물탱크를 생산하기 위해 끊임없이 노력해왔습니다.</p>
                <p>고객 여러분의 신뢰와 성원에 힘입어 전국 5,000여 곳 이상의 현장에 물탱크를 공급하며 업계 선두주자로 자리매김할 수 있었습니다.</p>
                <p>앞으로도 삼원테크는 품질과 기술 혁신을 통해 고객 여러분께 최상의 제품과 서비스를 제공하며, 깨끗한 물, 안전한 미래를 만들어가는 데 최선을 다하겠습니다.</p>
                <p className="font-semibold text-gray-900 text-xl mt-8">감사합니다.</p>
                <p className="text-gray-900 font-bold text-2xl">대표이사 김철수</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Section */}
      <div className="px-4 py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="text-center text-white">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-6xl`}></i>
                </div>
                <div className="text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-xl font-medium opacity-90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="px-4 py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div ref={missionRef} initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-3xl p-12 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 rounded-2xl mb-6">
                <i className="ri-flag-line text-3xl text-white"></i>
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">경영 이념</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">고객의 건강과 안전을 최우선으로 생각하며, 최고 품질의 물탱크를 제공하여 깨끗한 물 공급에 기여합니다.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3"><i className="ri-check-line text-2xl text-teal-600 mt-1"></i><span className="text-gray-700">고객 만족을 위한 최상의 품질 보증</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-2xl text-teal-600 mt-1"></i><span className="text-gray-700">위생적이고 안전한 식수 공급 시스템 구축</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-2xl text-teal-600 mt-1"></i><span className="text-gray-700">지속적인 기술 개발과 품질 향상</span></li>
              </ul>
            </motion.div>
            <motion.div ref={visionRef} initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-12 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-2xl mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">비전</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">대한민국을 넘어 글로벌 물탱크 시장을 선도하는 기업으로 성장하여, 전 세계인의 건강한 삶에 기여하는 것이 우리의 비전입니다.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3"><i className="ri-check-line text-2xl text-blue-600 mt-1"></i><span className="text-gray-700">국내 물탱크 시장 점유율 1위 달성</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-2xl text-blue-600 mt-1"></i><span className="text-gray-700">해외 시장 진출 및 글로벌 브랜드 구축</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-2xl text-blue-600 mt-1"></i><span className="text-gray-700">친환경 기술 선도 기업으로 성장</span></li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <motion.div ref={valuesRef} className="px-4 py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[2px] text-teal-600 font-semibold">Core Values</span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4">핵심 가치</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.15 }} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Certifications */}
      <div className="px-4 py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[2px] text-teal-600 font-semibold">Certifications</span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4">인증 및 자격</h2>
            <p className="text-xl text-gray-600 mt-4">국내외 공인 기관으로부터 인정받은 품질과 기술력</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }} className="bg-white border-2 border-gray-200 rounded-2xl p-8 text-center hover:border-teal-600 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-6">
                  <i className={`${cert.icon} text-4xl text-teal-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{cert.title}</h3>
                <p className="text-gray-600">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Facilities Section */}
      <div className="px-4 py-24 bg-gray-900 text-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm uppercase tracking-[2px] text-teal-400 font-semibold">Facilities</span>
              <h2 className="text-5xl font-bold mt-4 mb-6">최첨단 생산 시설</h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">최신 설비와 자동화 시스템을 갖춘 생산 공장에서 엄격한 품질 관리 하에 제품을 생산합니다.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0"><i className="ri-building-4-line text-2xl"></i></div>
                  <div><h4 className="text-xl font-bold mb-2">대규모 생산 공장</h4><p className="text-gray-400">10,000㎡ 규모의 현대식 생산 시설</p></div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0"><i className="ri-robot-line text-2xl"></i></div>
                  <div><h4 className="text-xl font-bold mb-2">자동화 생산 라인</h4><p className="text-gray-400">최신 자동화 설비로 정밀한 제작</p></div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0"><i className="ri-microscope-line text-2xl"></i></div>
                  <div><h4 className="text-xl font-bold mb-2">품질 검사 시스템</h4><p className="text-gray-400">전 공정 품질 관리 및 검사 체계</p></div>
                </li>
              </ul>
            </div>
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://readdy.ai/api/search-image?query=modern%20industrial%20manufacturing%20facility%20with%20stainless%20steel%20equipment%20automated%20production%20line%20clean%20bright%20factory%20interior%20professional%20industrial%20photography&width=900&height=1200&seq=facilities-samwon&orientation=portrait" alt="생산 시설" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative px-4 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=modern%20stainless%20steel%20water%20storage%20tanks%20in%20clean%20facility%20professional%20industrial%20photography%20bright%20lighting&width=1920&height=800&seq=cta-about-samwon&orientation=landscape" alt="문의하기" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-600/95 to-teal-700/95" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center text-white">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">프로젝트 문의하기</h2>
            <p className="text-xl md:text-2xl mb-10 leading-relaxed">최고 품질의 물탱크 솔루션을 제공해드립니다</p>
            <a href="/contact" className="inline-block bg-white text-teal-600 px-12 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl cursor-pointer whitespace-nowrap">상담 신청하기</a>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
